import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import './JobsCatalog.css';

function JobsCatalog() {
  const { hasPermission, apiFetch } = useAuth();
  const { language } = useI18n();
  
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Form states
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newJob, setNewJob] = useState({
    jobId: '',
    label: '',
    whitelisted: true,
    isGang: false,
    grades: [
      { grade: 0, name: 'employee', label: 'Employee', salary: 0 },
      { grade: 1, name: 'boss', label: 'Boss', salary: 0 }
    ],
    societyName: ''
  });

  const [editingJob, setEditingJob] = useState(null);
  const [editJobData, setEditJobData] = useState({});
  const [editingGrades, setEditingGrades] = useState(null); // jobId or null
  const [newGradeData, setNewGradeData] = useState({ grade: '', name: '', label: '', salary: '' });
  const [editingGrade, setEditingGrade] = useState(null); // { jobId, gradeNumber } or null
  const [editGradeData, setEditGradeData] = useState({});

  const canView = hasPermission('jobs.catalog.view');
  const canEdit = hasPermission('jobs.catalog.edit');

  const t = language === 'da'
    ? {
        jobsCatalog: 'Jobs katalog',
        createJob: 'Opret job',
        editJob: 'Rediger job',
        deleteJob: 'Slet job',
        jobId: 'Job ID',
        jobLabel: 'Job navn',
        whitelisted: 'Whitelisted',
        isGang: 'Adalah gang',
        grades: 'Grader',
        grade: 'Grade',
        name: 'Navn',
        label: 'Label',
        salary: 'Løn',
        societyName: 'Society navn',
        createBtn: 'Opret job',
        cancelBtn: 'Annuller',
        addGrade: 'Tilføj grade',
        removeGrade: 'Fjern grade',
        saving: 'Gemmer...',
        loadingCatalog: 'Indlæser jobs katalog...',
        noJobs: 'Ingen jobs fundet',
        successCreate: 'Job oprettet',
        successDelete: 'Job slettet',
        deleteConfirm: 'Er du sikker på at du vil slette dette job?',
        invalidJobId: 'Job ID kan kun indeholde bogstaver, tal og understreger',
        editJobName: 'Rediger jobnavn',
        newJobId: 'Nyt Job ID',
        saveName: 'Gem navn',
        societies: 'Society kontoer',
        societyCreated: 'Society konto oprettet',
        noPermission: 'Du har ikke adgang til dette område',
        error: 'Fejl',
        failedLoad: 'Kunne ikke indlæse jobs katalog',
        failedCreate: 'Kunne ikke oprette job',
        failedDelete: 'Kunne ikke slette job',
        jobExists: 'Job ID eksisterer allerede',
        editGrades: 'Rediger grader',
        addNewGrade: 'Tilføj ny grade',
        deleteGrade: 'Slet grade',
        editGradeSuccess: 'Grade opdateret',
        deleteGradeSuccess: 'Grade slettet',
        failedEditGrade: 'Kunne ikke opdatere grade',
        failedDeleteGrade: 'Kunne ikke slette grade'
      }
    : {
        jobsCatalog: 'Jobs Catalog',
        createJob: 'Create Job',
        editJob: 'Edit Job',
        deleteJob: 'Delete Job',
        jobId: 'Job ID',
        jobLabel: 'Job Name',
        whitelisted: 'Whitelisted',
        isGang: 'Is Gang',
        grades: 'Grades',
        grade: 'Grade',
        name: 'Name',
        label: 'Label',
        salary: 'Salary',
        societyName: 'Society Name',
        createBtn: 'Create Job',
        cancelBtn: 'Cancel',
        addGrade: 'Add Grade',
        removeGrade: 'Remove Grade',
        saving: 'Saving...',
        loadingCatalog: 'Loading jobs catalog...',
        noJobs: 'No jobs found',
        successCreate: 'Job created successfully',
        successDelete: 'Job deleted successfully',
        deleteConfirm: 'Are you sure you want to delete this job?',
        invalidJobId: 'Job ID can only contain letters, numbers, and underscores',
        editJobName: 'Edit Job Name',
        newJobId: 'New Job ID',
        saveName: 'Save Name',
        societies: 'Society Accounts',
        societyCreated: 'Society account created',
        noPermission: 'You do not have access to this area',
        error: 'Error',
        failedLoad: 'Failed to load jobs catalog',
        failedCreate: 'Failed to create job',
        failedDelete: 'Failed to delete job',
        jobExists: 'Job ID already exists',
        editGrades: 'Edit Grades',
        addNewGrade: 'Add New Grade',
        deleteGrade: 'Delete Grade',
        editGradeSuccess: 'Grade updated successfully',
        deleteGradeSuccess: 'Grade deleted successfully',
        failedEditGrade: 'Failed to update grade',
        failedDeleteGrade: 'Failed to delete grade'
      };

  const fetchCatalog = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiFetch('/api/jobs/catalog');
      if (!response.ok) {
        throw new Error(t.failedLoad);
      }
      const data = await response.json();
      // Map from fetchJobsCatalogForSite response format
      const jobs = data.jobs || [];
      const mappedJobs = Array.isArray(jobs) ? jobs.map(job => ({
        id: job.name,
        label: job.label,
        whitelisted: job.whitelisted,
        isGang: job.isGang,
        grades: job.grades || []
      })) : [];
      setCatalog(mappedJobs);
    } catch (err) {
      setError(err.message);
      setCatalog([]);
    } finally {
      setLoading(false);
    }
  }, [apiFetch, t.failedLoad]);

  useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);

  const validateJobId = (id) => {
    return /^[a-zA-Z0-9_]+$/.test(id);
  };

  const handleCreateJob = async () => {
    if (!newJob.jobId.trim() || !newJob.label.trim()) {
      setError('Job ID and Label are required');
      return;
    }

    if (!validateJobId(newJob.jobId)) {
      setError(t.invalidJobId);
      return;
    }

    if (newJob.grades.length === 0) {
      setError('At least one grade is required');
      return;
    }

    setActionLoading(true);
    try {
      const response = await apiFetch('/api/jobs/catalog', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jobId: newJob.jobId,
          label: newJob.label,
          whitelisted: newJob.whitelisted,
          isGang: newJob.isGang,
          grades: newJob.grades,
          societyName: newJob.societyName || `society_${newJob.jobId}`
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || t.failedCreate);
      }

      setNotice(t.successCreate);
      setShowCreateForm(false);
      setNewJob({
        jobId: '',
        label: '',
        whitelisted: true,
        isGang: false,
        grades: [
          { grade: 0, name: 'employee', label: 'Employee', salary: 0 },
          { grade: 1, name: 'boss', label: 'Boss', salary: 0 }
        ],
        societyName: ''
      });
      fetchCatalog();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm(t.deleteConfirm)) return;

    setActionLoading(true);
    try {
      const response = await apiFetch(`/api/jobs/catalog/${encodeURIComponent(jobId)}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || t.failedDelete);
      }

      setNotice(t.successDelete);
      fetchCatalog();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateJobName = async (jobId) => {
    if (!editJobData.newJobId.trim()) {
      setError('New Job ID is required');
      return;
    }

    if (!validateJobId(editJobData.newJobId)) {
      setError(t.invalidJobId);
      return;
    }

    setActionLoading(true);
    try {
      const response = await apiFetch(`/api/jobs/catalog/${encodeURIComponent(jobId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          newJobId: editJobData.newJobId,
          label: editJobData.label
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || t.failedCreate);
      }

      setNotice('Job updated');
      setEditingJob(null);
      fetchCatalog();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddGrade = async (jobId) => {
    if (!newGradeData.grade && newGradeData.grade !== 0) {
      setError('Grade number is required');
      return;
    }

    if (!newGradeData.name.trim() || !newGradeData.label.trim()) {
      setError('Grade name and label are required');
      return;
    }

    setActionLoading(true);
    try {
      const response = await apiFetch(`/api/jobs/catalog/${encodeURIComponent(jobId)}/grades`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          grade: Number(newGradeData.grade),
          name: newGradeData.name,
          label: newGradeData.label,
          salary: Number(newGradeData.salary) || 0
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || t.failedEditGrade);
      }

      setNotice(t.editGradeSuccess);
      setNewGradeData({ grade: '', name: '', label: '', salary: '' });
      fetchCatalog();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteGrade = async (jobId, gradeNumber) => {
    if (!window.confirm(`${t.deleteConfirm}?`)) return;

    setActionLoading(true);
    try {
      const response = await apiFetch(`/api/jobs/catalog/${encodeURIComponent(jobId)}/grades/${encodeURIComponent(gradeNumber)}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || t.failedDeleteGrade);
      }

      setNotice(t.deleteGradeSuccess);
      fetchCatalog();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateGrade = async (jobId, oldGradeNumber) => {
    if (!editGradeData.grade && editGradeData.grade !== 0) {
      setError('Grade number is required');
      return;
    }

    if (!editGradeData.name.trim() || !editGradeData.label.trim()) {
      setError('Grade name and label are required');
      return;
    }

    setActionLoading(true);
    try {
      const response = await apiFetch(`/api/jobs/catalog/${encodeURIComponent(jobId)}/grades/${encodeURIComponent(oldGradeNumber)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          grade: Number(editGradeData.grade),
          name: editGradeData.name,
          label: editGradeData.label,
          salary: Number(editGradeData.salary) || 0
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || t.failedEditGrade);
      }

      setNotice(t.editGradeSuccess);
      setEditingGrade(null);
      setEditGradeData({});
      fetchCatalog();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (!canView) {
    return <div className="jobs-catalog"><div className="jobs-empty">{t.noPermission}</div></div>;
  }

  return (
    <div className="jobs-catalog">
      <div className="jobs-header">
        <h2>{t.jobsCatalog}</h2>
        <button 
          onClick={() => setShowCreateForm(!showCreateForm)}
          disabled={actionLoading || !canEdit}
          className="btn-primary"
        >
          {showCreateForm ? t.cancelBtn : t.createJob}
        </button>
      </div>

      {error && <div className="alert error">{error}</div>}
      {notice && <div className="alert success">{notice}</div>}

      {showCreateForm && (
        <div className="create-form">
          <div className="form-group">
            <label>{t.jobId}</label>
            <input
              type="text"
              value={newJob.jobId}
              onChange={(e) => setNewJob({ ...newJob, jobId: e.target.value })}
              placeholder="e.g. priest, mechanic, police"
              disabled={actionLoading}
            />
          </div>

          <div className="form-group">
            <label>{t.jobLabel}</label>
            <input
              type="text"
              value={newJob.label}
              onChange={(e) => setNewJob({ ...newJob, label: e.target.value })}
              placeholder="e.g. Kirken, Mekanik, Politi"
              disabled={actionLoading}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={newJob.whitelisted}
                  onChange={(e) => setNewJob({ ...newJob, whitelisted: e.target.checked })}
                  disabled={actionLoading}
                />
                {t.whitelisted}
              </label>
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={newJob.isGang}
                  onChange={(e) => setNewJob({ ...newJob, isGang: e.target.checked })}
                  disabled={actionLoading}
                />
                {t.isGang}
              </label>
            </div>
          </div>

          <div className="form-group">
            <label>{t.societyName}</label>
            <input
              type="text"
              value={newJob.societyName}
              onChange={(e) => setNewJob({ ...newJob, societyName: e.target.value })}
              placeholder={`society_${newJob.jobId || 'jobname'}`}
              disabled={actionLoading}
            />
          </div>

          <div className="form-group">
            <label>{t.grades}</label>
            {newJob.grades.map((grade, idx) => (
              <div key={idx} className="grade-input">
                <input
                  type="number"
                  placeholder="Grade"
                  value={grade.grade}
                  onChange={(e) => {
                    const updated = [...newJob.grades];
                    updated[idx].grade = Number(e.target.value);
                    setNewJob({ ...newJob, grades: updated });
                  }}
                  disabled={actionLoading}
                />
                <input
                  type="text"
                  placeholder="Name"
                  value={grade.name}
                  onChange={(e) => {
                    const updated = [...newJob.grades];
                    updated[idx].name = e.target.value;
                    setNewJob({ ...newJob, grades: updated });
                  }}
                  disabled={actionLoading}
                />
                <input
                  type="text"
                  placeholder="Label"
                  value={grade.label}
                  onChange={(e) => {
                    const updated = [...newJob.grades];
                    updated[idx].label = e.target.value;
                    setNewJob({ ...newJob, grades: updated });
                  }}
                  disabled={actionLoading}
                />
                <input
                  type="number"
                  placeholder="Salary"
                  value={grade.salary}
                  onChange={(e) => {
                    const updated = [...newJob.grades];
                    updated[idx].salary = Number(e.target.value);
                    setNewJob({ ...newJob, grades: updated });
                  }}
                  disabled={actionLoading}
                />
                <button
                  onClick={() => {
                    const updated = newJob.grades.filter((_, i) => i !== idx);
                    setNewJob({ ...newJob, grades: updated });
                  }}
                  className="btn-danger"
                  disabled={actionLoading || newJob.grades.length === 1}
                >
                  Delete
                </button>
              </div>
            ))}
            <button
              onClick={() => {
                const maxGrade = Math.max(...newJob.grades.map(g => g.grade), -1);
                setNewJob({
                  ...newJob,
                  grades: [
                    ...newJob.grades,
                    { grade: maxGrade + 1, name: `grade_${maxGrade + 1}`, label: `Grade ${maxGrade + 1}`, salary: 0 }
                  ]
                });
              }}
              className="btn-secondary"
              disabled={actionLoading}
            >
              {t.addGrade}
            </button>
          </div>

          <button
            onClick={handleCreateJob}
            disabled={actionLoading}
            className="btn-primary"
          >
            {actionLoading ? t.saving : t.createBtn}
          </button>
        </div>
      )}

      {loading ? (
        <div className="jobs-empty">{t.loadingCatalog}</div>
      ) : !Array.isArray(catalog) || catalog.length === 0 ? (
        <div className="jobs-empty">{t.noJobs}</div>
      ) : (
        <div className="jobs-list">
          {catalog.map((job) => (
            <div key={job.id} className="job-card">
              <div className="job-header">
                <div>
                  <h3>{job.label}</h3>
                  <p className="job-id">{job.id}</p>
                  {job.whitelisted && <span className="badge whitelisted">Whitelisted</span>}
                  {job.isGang && <span className="badge gang">Gang</span>}
                </div>
                <div className="job-actions">
                  <button
                    onClick={() => setEditingJob(job.id)}
                    className="btn-secondary"
                    disabled={actionLoading || !canEdit}
                  >
                    {t.editJob}
                  </button>
                  <button
                    onClick={() => handleDeleteJob(job.id)}
                    className="btn-danger"
                    disabled={actionLoading || !canEdit}
                  >
                    {t.deleteJob}
                  </button>
                </div>
              </div>

              {editingJob === job.id && (
                <div className="edit-form">
                  <div className="form-group">
                    <label>{t.jobLabel}</label>
                    <input
                      type="text"
                      value={editJobData.label || job.label}
                      onChange={(e) => setEditJobData({ ...editJobData, label: e.target.value })}
                      disabled={actionLoading}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t.newJobId}</label>
                    <input
                      type="text"
                      value={editJobData.newJobId || job.id}
                      onChange={(e) => setEditJobData({ ...editJobData, newJobId: e.target.value })}
                      disabled={actionLoading}
                    />
                  </div>
                  <div className="form-actions">
                    <button
                      onClick={() => handleUpdateJobName(job.id)}
                      disabled={actionLoading}
                      className="btn-primary"
                    >
                      {actionLoading ? t.saving : t.saveName}
                    </button>
                    <button
                      onClick={() => setEditingJob(null)}
                      disabled={actionLoading}
                      className="btn-secondary"
                    >
                      {t.cancelBtn}
                    </button>
                  </div>
                </div>
              )}

              <div className="grades-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h4>{t.grades}</h4>
                  <button
                    onClick={() => setEditingGrades(editingGrades === job.id ? null : job.id)}
                    className="btn-secondary"
                    style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }}
                    disabled={actionLoading || !canEdit}
                  >
                    {editingGrades === job.id ? t.cancelBtn : t.editGrades}
                  </button>
                </div>

                {editingGrades === job.id && (
                  <div className="edit-form" style={{ marginBottom: '1rem' }}>
                    <div className="form-row">
                      <div className="form-group">
                        <label>{t.grade}</label>
                        <input
                          type="number"
                          value={newGradeData.grade}
                          onChange={(e) => setNewGradeData({ ...newGradeData, grade: e.target.value })}
                          placeholder="e.g. 0, 1, 2"
                          disabled={actionLoading}
                        />
                      </div>
                      <div className="form-group">
                        <label>{t.name}</label>
                        <input
                          type="text"
                          value={newGradeData.name}
                          onChange={(e) => setNewGradeData({ ...newGradeData, name: e.target.value })}
                          placeholder="e.g. employee"
                          disabled={actionLoading}
                        />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>{t.label}</label>
                        <input
                          type="text"
                          value={newGradeData.label}
                          onChange={(e) => setNewGradeData({ ...newGradeData, label: e.target.value })}
                          placeholder="e.g. Employee"
                          disabled={actionLoading}
                        />
                      </div>
                      <div className="form-group">
                        <label>{t.salary}</label>
                        <input
                          type="number"
                          value={newGradeData.salary}
                          onChange={(e) => setNewGradeData({ ...newGradeData, salary: e.target.value })}
                          placeholder="e.g. 500"
                          disabled={actionLoading}
                        />
                      </div>
                    </div>
                    <button
                      onClick={() => handleAddGrade(job.id)}
                      disabled={actionLoading}
                      className="btn-primary"
                    >
                      {actionLoading ? t.saving : t.addNewGrade}
                    </button>
                  </div>
                )}

                {job.grades.length === 0 ? (
                  <p className="no-grades">No grades defined</p>
                ) : (
                  <>
                    <table className="grades-table">
                      <thead>
                        <tr>
                          <th>{t.grade}</th>
                          <th>{t.name}</th>
                          <th>{t.label}</th>
                          <th>{t.salary}</th>
                          {editingGrades === job.id && <th style={{ width: '120px' }}>Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {job.grades.map((grade) => (
                          <React.Fragment key={`${job.id}-${grade.grade}`}>
                            <tr style={{ cursor: editingGrades === job.id ? 'pointer' : 'default' }}>
                              <td>{grade.grade}</td>
                              <td>{grade.name}</td>
                              <td>{grade.label}</td>
                              <td>{grade.salary}</td>
                              {editingGrades === job.id && (
                                <td style={{ textAlign: 'center' }}>
                                  <button
                                    onClick={() => {
                                      if (editingGrade?.jobId === job.id && editingGrade?.gradeNumber === grade.grade) {
                                        setEditingGrade(null);
                                        setEditGradeData({});
                                      } else {
                                        setEditingGrade({ jobId: job.id, gradeNumber: grade.grade });
                                        setEditGradeData({
                                          grade: grade.grade,
                                          name: grade.name,
                                          label: grade.label,
                                          salary: grade.salary
                                        });
                                      }
                                    }}
                                    className="btn-secondary"
                                    style={{ padding: '0.4rem 0.8rem', fontSize: '0.85rem', marginRight: '4px' }}
                                    disabled={actionLoading}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => handleDeleteGrade(job.id, grade.grade)}
                                    className="btn-danger"
                                    style={{ padding: '0.4rem 0.8rem', fontSize: '0.85rem' }}
                                    disabled={actionLoading}
                                  >
                                    Delete
                                  </button>
                                </td>
                              )}
                            </tr>
                            {editingGrade?.jobId === job.id && editingGrade?.gradeNumber === grade.grade && (
                              <tr>
                                <td colSpan={editingGrades === job.id ? 5 : 4}>
                                  <div className="edit-form" style={{ margin: '10px 0' }}>
                                    <div className="form-row">
                                      <div className="form-group">
                                        <label>{t.grade}</label>
                                        <input
                                          type="number"
                                          value={editGradeData.grade}
                                          onChange={(e) => setEditGradeData({ ...editGradeData, grade: e.target.value })}
                                          disabled={actionLoading}
                                        />
                                      </div>
                                      <div className="form-group">
                                        <label>{t.name}</label>
                                        <input
                                          type="text"
                                          value={editGradeData.name}
                                          onChange={(e) => setEditGradeData({ ...editGradeData, name: e.target.value })}
                                          disabled={actionLoading}
                                        />
                                      </div>
                                    </div>
                                    <div className="form-row">
                                      <div className="form-group">
                                        <label>{t.label}</label>
                                        <input
                                          type="text"
                                          value={editGradeData.label}
                                          onChange={(e) => setEditGradeData({ ...editGradeData, label: e.target.value })}
                                          disabled={actionLoading}
                                        />
                                      </div>
                                      <div className="form-group">
                                        <label>{t.salary}</label>
                                        <input
                                          type="number"
                                          value={editGradeData.salary}
                                          onChange={(e) => setEditGradeData({ ...editGradeData, salary: e.target.value })}
                                          disabled={actionLoading}
                                        />
                                      </div>
                                    </div>
                                    <div className="form-actions">
                                      <button
                                        onClick={() => handleUpdateGrade(job.id, grade.grade)}
                                        disabled={actionLoading}
                                        className="btn-primary"
                                      >
                                        {actionLoading ? t.saving : 'Save'}
                                      </button>
                                      <button
                                        onClick={() => {
                                          setEditingGrade(null);
                                          setEditGradeData({});
                                        }}
                                        disabled={actionLoading}
                                        className="btn-secondary"
                                      >
                                        {t.cancelBtn}
                                      </button>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default JobsCatalog;
